import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/shared/components/ui/form'
import { Button } from '@/shared/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import {
  changeRoleSchema,
  type ChangeRoleFormValues,
} from '../schemas/userSchemas'
import {
  ASSIGNABLE_ROLES,
  ROLE_DESCRIPTIONS,
  ROLE_LABELS,
  type AssignableRole,
} from '../constants/assignableRoles'
import { useUpdateOrgMemberRole } from '../hooks/useUpdateOrgMemberRole'
import type { OrgMember } from '../api/operations'

interface ChangeRoleDialogProps {
  member: OrgMember | null
  onOpenChange: (open: boolean) => void
}

function isAssignableRole(name: string): name is AssignableRole {
  return (ASSIGNABLE_ROLES as readonly string[]).includes(name)
}

export function ChangeRoleDialog({ member, onOpenChange }: ChangeRoleDialogProps) {
  const initialRole: AssignableRole =
    member && isAssignableRole(member.role) ? member.role : 'ORG_MANAGER'

  const form = useForm<ChangeRoleFormValues>({
    resolver: zodResolver(changeRoleSchema),
    defaultValues: { role: initialRole },
    mode: 'onBlur',
  })

  useEffect(() => {
    if (member) form.reset({ role: initialRole })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [member?.id])

  const { updateRole, loading } = useUpdateOrgMemberRole({
    onSuccess: () => onOpenChange(false),
  })

  const handleSubmit = form.handleSubmit(async (values) => {
    if (!member) return
    await updateRole({ memberId: member.id, newRole: values.role })
  })

  return (
    <Dialog open={!!member} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Cambiar rol</DialogTitle>
          <DialogDescription>
            {member ? (
              <>
                Estás cambiando el rol de <strong>{member.email}</strong>.
              </>
            ) : null}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={handleSubmit} className="grid gap-5">
            <FormField
              control={form.control}
              name="role"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nuevo rol</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {ASSIGNABLE_ROLES.map((role) => (
                        <SelectItem key={role} value={role}>
                          {ROLE_LABELS[role]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormDescription>{ROLE_DESCRIPTIONS[field.value]}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="ghost"
                onClick={() => onOpenChange(false)}
                disabled={loading}
              >
                Cancelar
              </Button>
              <Button type="submit" variant="primary" disabled={loading}>
                {loading ? 'Guardando…' : 'Guardar cambios'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
